
// src/pages/Courses/components/LiveClassesModal.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const LiveClassesModal = ({ course, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form states - only startTime (scheduledStartTime) needed
  const [newLiveClass, setNewLiveClass] = useState({
    title: '',
    description: '',
    duration: 60,
    maxParticipants: 100,
    scheduledStartTime: '' // Changed from startTime to scheduledStartTime
  });

  // Initialize form with default time
  useEffect(() => {
    const now = new Date();
    const defaultStartTime = new Date(now.getTime() + 30 * 60000); // 30 minutes from now
    
    setNewLiveClass(prev => ({
      ...prev,
      scheduledStartTime: formatDateTimeForInput(defaultStartTime)
    }));
  }, []);

  const getAuthToken = () => {
    return localStorage.getItem('trainertoken');
  };

  // Format date for datetime-local input
  const formatDateTimeForInput = (date) => {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  // Create Live Session
  const handleCreateLiveSession = async () => {
    if (!newLiveClass.title.trim()) {
      setError('Please provide a session title');
      return;
    }

    if (!newLiveClass.description.trim()) {
      setError('Please provide a session description');
      return;
    }

    if (!newLiveClass.scheduledStartTime) {
      setError('Please select a start time');
      return;
    }

    const startTime = new Date(newLiveClass.scheduledStartTime);
    
    if (startTime <= new Date()) {
      setError('Start time must be in the future');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = getAuthToken();
      
      const response = await axios.post(
        `${API_BASE_URL}/courseLiveSession/create`,
        {
          title: newLiveClass.title,
          description: newLiveClass.description,
          courseId: course._id,
          duration: parseInt(newLiveClass.duration),
          maxParticipants: parseInt(newLiveClass.maxParticipants),
          scheduledStartTime: startTime.toISOString() // Send as scheduledStartTime
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.success) {
        const createdSession = response.data.data;
        
        // Reset form
        const now = new Date();
        const defaultStartTime = new Date(now.getTime() + 30 * 60000);
        
        setNewLiveClass({
          title: '',
          description: '',
          duration: 60,
          maxParticipants: 100,
          scheduledStartTime: formatDateTimeForInput(defaultStartTime)
        });
        
        // Show success
        setSuccessMessage('✅ Live session created successfully!');
        
        // Show session details in toast
        toast.success(
          <div className="space-y-2">
            <p className="font-semibold">🎉 Live Session Created!</p>
            <div className="bg-white p-3 rounded-lg border border-green-200">
              <p className="text-sm font-medium mb-2">Session Details:</p>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Title:</span> {createdSession.title}</p>
                <p><span className="font-medium">Scheduled Time:</span> {formatDateTime(createdSession.scheduledStartTime)}</p>
                <p><span className="font-medium">Duration:</span> {createdSession.duration} minutes</p>
                <p><span className="font-medium">Room Code:</span> <code className="bg-gray-100 px-2 py-1 rounded">{createdSession.roomCode}</code></p>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Session has been scheduled. You can manage it from the course dashboard.
              </p>
            </div>
          </div>,
          { 
            autoClose: 8000,
            closeButton: true,
            draggable: false 
          }
        );
        
        // Refresh data
        if (typeof onSuccess === 'function') {
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
        
        // Close modal after success
        setTimeout(() => {
          onClose();
        }, 2000);
        
      } else {
        setError(response.data?.message || 'Failed to create live session');
      }
    } catch (err) {
      console.error('Error creating live session:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create live session';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div className="bg-card rounded-xl border border-border shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">
              Create Live Session
            </h2>
            <p className="text-muted-foreground mt-1">
              {course?.title} - Schedule a new live class
            </p>
          </div>
          <button
            onClick={handleCloseClick}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-200 cursor-pointer"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg dark:bg-green-900/20 dark:border-green-800 dark:text-green-400">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="p-6">
          <div className="space-y-6">
            <div className="bg-muted/20 p-4 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">Live Session Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Session Title *
                  </label>
                  <input
                    type="text"
                    value={newLiveClass.title}
                    onChange={(e) => setNewLiveClass(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter session title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    value={newLiveClass.duration}
                    onChange={(e) => setNewLiveClass(prev => ({ ...prev, duration: e.target.value }))}
                    min="5"
                    max="480"
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Scheduled Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={newLiveClass.scheduledStartTime}
                    onChange={(e) => setNewLiveClass(prev => ({ ...prev, scheduledStartTime: e.target.value }))}
                    required
                    min={formatDateTimeForInput(new Date())}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Session will be scheduled for this time. End time will be automatically calculated based on duration.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newLiveClass.description}
                    onChange={(e) => setNewLiveClass(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-vertical"
                    placeholder="Describe what this session will cover..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Max Participants *
                  </label>
                  <input
                    type="number"
                    value={newLiveClass.maxParticipants}
                    onChange={(e) => setNewLiveClass(prev => ({ ...prev, maxParticipants: e.target.value }))}
                    min="1"
                    max="500"
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">ℹ️ Important Notes:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Session will be scheduled for the selected start time</li>
                  <li>• You can manage sessions from the course dashboard</li>
                  <li>• Participants must be enrolled in this course to join</li>
                  <li>• Session will be automatically linked to this course</li>
                  <li>• Session will be created with status "SCHEDULED"</li>
                  <li>• End time will be automatically calculated (start time + duration)</li>
                </ul>
              </div>

              <div className="flex items-center space-x-4 mt-6">
                <button
                  onClick={handleCreateLiveSession}
                  disabled={loading || !newLiveClass.title.trim() || !newLiveClass.description.trim() || !newLiveClass.scheduledStartTime}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2 cursor-pointer"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating Session...</span>
                    </>
                  ) : (
                    <>
                      <span>📅</span>
                      <span>Schedule Live Session</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleCloseClick}
                  className="px-4 py-3 border border-border rounded-lg text-foreground hover:bg-muted transition-colors duration-200 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Course ID: <code className="bg-gray-100 px-2 py-1 rounded">{course?._id}</code>
          </div>
          <div className="text-sm text-muted-foreground">
            Live sessions will appear in course dashboard
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveClassesModal;